import * as React from 'react'

import {
  FinanceAccountingModuleRoutesVisibilityDocument,
  LedgerAccountsDetailPageRouteDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const LedgerAccountsPage = React.lazy(() => import('modules/finance/pages/Accounting/AccountsPage'))
const LedgerAccountDetailPage = React.lazy(
  () => import('modules/finance/pages/LedgerAccountDetailPage')
)

const GeneralLedgerPage = React.lazy(
  () => import('modules/finance/pages/Accounting/GeneralLedgerPage')
)

const AccountingOverviewPage = React.lazy(
  () => import('modules/finance/pages/AccountingOverviewPage')
)

const DraftEntriesPage = React.lazy(
  () => import('modules/finance/pages/Accounting/DraftEntriesPage')
)
const RootPage = React.lazy(() => import('modules/finance/pages/Accounting/RootPage'))

export const financeAccountingRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.finance.accounting.path,
      element: <RootPage />,
      handle: {
        module: 'finance-accounting',
        moduleName: () => i18n.t('sidebar.finance_accounting'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: FinanceAccountingModuleRoutesVisibilityDocument,
          }).map(({ data }) => {
            const canSeeAccounts = data?.finance?._policies?.accountingSeeAccounts?.can ?? false
            const canManageJournalEntries =
              data?.finance?._policies?.journalEntriesManage?.can ?? false
            const canSeeAccountingSettings =
              data?.finance?._policies?.settingsSeeAccountingSettings?.can

            const settings = canSeeAccountingSettings
              ? navigation.settings.finance.accounting.fullPath()
              : undefined

            return {
              title: i18n.t('sidebar.finance_accounting'),
              settings: settings,
              subNavigation: [
                {
                  index: true,
                  id: 'overview',
                  title: i18n.t('finance.modules.treasury.overview'),
                  href: navigation.finance.accounting.overview.fullPath(),
                  isVisible: canSeeAccounts,
                },
                {
                  id: 'chart-accounts',
                  title: i18n.t('finance.modules.accounting.chart_of_accounts'),
                  href: navigation.finance.accounting.ledgerAccounts.fullPath(),
                  isVisible: canSeeAccounts,
                },
                {
                  id: 'general-ledger',
                  title: i18n.t('finance.modules.accounting.general_ledger'),
                  href: navigation.finance.accounting.generalLedger.fullPath(),
                  isVisible: canManageJournalEntries,
                },
                {
                  id: 'draft-entries',
                  title: i18n.t('finance.modules.accounting.draft_entries'),
                  href: navigation.finance.accounting.draftEntries.fullPath(),
                  isVisible: canManageJournalEntries,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          path: navigation.finance.accounting.overview.path,
          element: <AccountingOverviewPage />,
        },
        {
          path: navigation.finance.accounting.ledgerAccounts.path,
          element: <LedgerAccountsPage />,
          handle: {
            meta: () => ({ title: i18n.t('finance.modules.accounting.chart_of_accounts') }),
          },
        },
        {
          path: navigation.finance.accounting.generalLedger.path,
          element: <GeneralLedgerPage />,
          handle: {
            meta: () => ({ title: i18n.t('finance.modules.accounting.general_ledger') }),
          },
        },
        {
          path: navigation.finance.accounting.draftEntries.path,
          element: <DraftEntriesPage />,
          handle: {
            meta: () => ({ title: i18n.t('finance.modules.accounting.draft_entries') }),
          },
        },
        {
          path: navigation.finance.accounting.ledgerAccountId.path,
          element: <LedgerAccountDetailPage />,
          handle: {
            resource: true,
            meta: ({ params, watchQuery }) => {
              return watchQuery({
                query: LedgerAccountsDetailPageRouteDocument,
                variables: {
                  id: Number(params['ledgerAccountId']),
                },
              }).map(({ data }) => {
                const ledgerAccount = data?.finance?.accounts[0]
                return {
                  title: (ledgerAccount?.number ?? '') + ' - ' + (ledgerAccount?.name ?? ''),
                  illustration: 'multiple-buildings',
                  description: i18n.t('finance.ledger_account_detail.description', {
                    number: ledgerAccount?.number || '',
                  }),
                }
              })
            },
          },
        },
      ],
    }),
  ]
})
